import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface ConfirmRequest {
  title: string;
  /** What actually happens, in the operator's terms. */
  body?: string;
  confirmLabel?: string;
  /** Destructive actions get the danger styling and a slower default. */
  tone?: 'default' | 'danger';
}

interface PromptRequest extends ConfirmRequest {
  label: string;
  placeholder?: string;
  initial?: string;
  /** Return a message to block submission, or null to allow it. */
  validate?: (value: string) => string | null;
}

interface Api {
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  prompt: (request: PromptRequest) => Promise<string | null>;
}

const Ctx = createContext<Api | null>(null);

type Pending =
  | { kind: 'confirm'; request: ConfirmRequest; resolve: (value: boolean) => void }
  | { kind: 'prompt'; request: PromptRequest; resolve: (value: string | null) => void };

/**
 * Confirmations and short inputs, in the product's own dialog.
 *
 * `window.confirm` and `window.prompt` were doing this job. They cannot be
 * translated (the OS supplies "OK"/"Cancel" in the browser's language, not the
 * venue's), they cannot be styled, they block the whole tab, and on a till in
 * kiosk mode some browsers suppress them outright — so a destructive action
 * would either look foreign or silently do nothing.
 *
 * The promise-based API keeps call sites as short as the native ones:
 *   `if (!(await confirm({ title }))) return;`
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Held in a ref so a resolved promise is never resolved twice by a late close.
  const settled = useRef(false);

  const close = useCallback(() => {
    setPending(null);
    setValue('');
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    if (!pending || settled.current) return close();
    settled.current = true;
    if (pending.kind === 'confirm') pending.resolve(false);
    else pending.resolve(null);
    close();
  }, [close, pending]);

  const api = useMemo<Api>(
    () => ({
      confirm: (request) =>
        new Promise<boolean>((resolve) => {
          settled.current = false;
          setPending({ kind: 'confirm', request, resolve });
        }),
      prompt: (request) =>
        new Promise<string | null>((resolve) => {
          settled.current = false;
          setValue(request.initial ?? '');
          setPending({ kind: 'prompt', request, resolve });
        }),
    }),
    [],
  );

  const accept = useCallback(() => {
    if (!pending || settled.current) return;
    if (pending.kind === 'prompt') {
      const problem = pending.request.validate?.(value) ?? null;
      if (problem) {
        setError(problem);
        return;
      }
      settled.current = true;
      pending.resolve(value);
    } else {
      settled.current = true;
      pending.resolve(true);
    }
    close();
  }, [close, pending, value]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {pending && (
        <Dialog
          open
          onClose={cancel}
          title={pending.request.title}
          description={pending.request.body}
          className="max-w-md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={cancel}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={pending.request.tone === 'danger' ? 'danger' : 'default'}
                onClick={accept}
              >
                {pending.request.confirmLabel ?? t('common.confirm')}
              </Button>
            </div>
          }
        >
          {pending.kind === 'prompt' ? (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="confirm-prompt">
                {pending.request.label}
              </label>
              <Input
                id="confirm-prompt"
                autoFocus
                value={value}
                placeholder={pending.request.placeholder}
                onChange={(event) => {
                  setValue(event.target.value);
                  setError(null);
                }}
                onKeyDown={(event) => event.key === 'Enter' && accept()}
              />
              {error && <p className="text-sm text-[var(--destructive)]">{error}</p>}
            </div>
          ) : null}
        </Dialog>
      )}
    </Ctx.Provider>
  );
}

/**
 * Falls back to the native dialogs when no provider is mounted, so a component
 * rendered outside the app shell still asks rather than acting silently.
 */
export function useConfirm(): Api {
  const ctx = useContext(Ctx);
  return (
    ctx ?? {
      confirm: async ({ title, body }) => window.confirm([title, body].filter(Boolean).join('\n\n')),
      prompt: async ({ title, initial }) => window.prompt(title, initial ?? ''),
    }
  );
}
